import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Tag,
  Button,
  Space,
  message,
  Tooltip,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { DownloadOutlined, CloseOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table/interface";
import { PlanogramTask, TaskDetail } from "./task";
import { TaskService } from "../../services/taskService";
import * as XLSX from "xlsx";
import dayjs from 'dayjs';

const { Title } = Typography;

// 商品清单项接口
interface ProductListItem {
  key: string;
  productCode: string;
  productName: string;
  plannedQuantity: number;
  operationType: "OUT" | "IN" | "REARRANGE" | "NEW_ITEM";
  levelCode?: string;
  xyzQuantity: string;
  isRestocked: boolean;
  updateTime: string;
}

interface ProductListModalProps {
  visible: boolean;
  onClose: () => void;
  task: PlanogramTask | null;
}

const ProductListModal: React.FC<ProductListModalProps> = ({
  visible,
  onClose,
  task,
}) => {
  const { t } = useTranslation();
  const [productList, setProductList] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const formatDateTime = (timestamp: number): string => {
    if (!timestamp) {
      return '';
    }
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };
    
  // 将TaskDetail转换为ProductListItem
  const transformTaskDetailsToProductList = (
    taskDetails: TaskDetail[]
  ): ProductListItem[] => {
    return taskDetails.map((detail, index) => {
      // 根据actionType确定operationType
      let operationType = detail.actionType;

      // 生成商品编码
      const productCode = detail.itemCode;

      // 计算计划处理数量
      const plannedQuantity = `${detail.plannedXFacings}/${detail.plannedYFacings}/${detail.plannedZFacings}`
      const plannedCount = detail.plannedXFacings * detail.plannedYFacings * detail.plannedZFacings

      // 生成XYZ数量字符串
      const xyzQuantity = `${detail.actualXFacings}/${detail.actualYFacings}/${detail.actualZFacings}`;
      const actualQuantity = detail.actualXFacings * detail.actualYFacings * detail.actualZFacings;

      // Level Code (MoveOut没有目标层位)
      const levelCode = detail.shelfLevel;
      const layerSeq = detail.targetLayerSeq;

      const isRestocked = "completed" === detail.status; 

      const updateTime = detail.gmtModified;

      return {
        key: detail.id,
        productCode,
        productName: detail.itemName,
        plannedQuantity,
        plannedCount,
        operationType,
        levelCode,
        layerSeq,
        xyzQuantity,
        actualQuantity,
        isRestocked,
        updateTime,
        hasDiff: detail.hasDiff,
        workerName: detail.workerName,
        targetLayerSeq: detail.targetLayerSeq,
      };
    });
  };

  // 加载商品清单数据
  useEffect(() => {
    if (visible && task) {
      setLoading(true);
      // 使用TaskService获取数据，支持mock和真实数据
      TaskService.getProductList(task.shop, task.taskCode)
        .then((response) => {
          if (response.success && response.data) {
            const transformedList = transformTaskDetailsToProductList(response.data);
            setProductList(transformedList);
          } else {
            console.warn("获取商品清单数据失败:", response.errorMessage);
            message.warning(t("taskManagement.list.productListModal.loadFailed"));
            // 即使没有数据也清空列表，避免显示旧数据
            setProductList([]);
          }
        })
        .catch((error) => {
          console.error("获取商品清单数据异常:", error);
          message.error(t("taskManagement.list.productListModal.loadFailed"));
          // 出错时也清空列表
          setProductList([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // 当没有任务或不可见时清空列表
      setProductList([]);
    }
  }, [visible, task]);

  // 导出Excel
  const handleExportExcel = () => {
    if (productList.length === 0) {
      message.warning(t("taskManagement.list.productListModal.noDataToExport"));
      return;
    }

    try {
      // 准备导出数据
      const exportData = productList.map((item) => ({
        [t("taskManagement.list.productListModal.columns.itemCode")]: item.productCode,
        [t("taskManagement.list.productListModal.columns.itemName")]: item.productName,
        [t("taskManagement.list.productListModal.columns.operationType")]: item.operationType,
        ["Level Code"]: item.levelCode || "-",
        [t("taskManagement.list.productListModal.columns.operationType")]: item.operationType,
        ["Position"]: item.targetLayerSeq || "-",
        [t("taskManagement.list.productListModal.columns.plannedQuantity")]: item.plannedQuantity,
        [t("taskManagement.list.productListModal.columns.plannedCount")]: item.plannedCount,
        [t("taskManagement.list.productListModal.columns.xyzQuantity")]: item.xyzQuantity,
        [t("taskManagement.list.productListModal.columns.actualQuantity")]: item.actualQuantity,
        ["Has Diff"]: item.hasDiff,
        [t("taskManagement.list.productListModal.columns.isRestockCompleted")]:
          item.isRestocked
            ? t("taskManagement.list.productListModal.restockStatus.yes")
            : t("taskManagement.list.productListModal.restockStatus.no"),
        [t("taskManagement.list.productListModal.columns.updateTime")]: formatDateTime(item.updateTime),
        ["Worker Name"]: item.workerName,
      }));

      // 创建工作簿
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        ws,
        t("taskManagement.list.productListModal.title")
      );

      // 设置列宽
      const colWidths = [
        { wch: 12 }, // 商品编码
        { wch: 25 }, // 商品名称
        { wch: 12 }, // 计划处理数量
        { wch: 12 }, // 作业类型
        { wch: 12 }, // Level Code
        { wch: 12 }, // XYZ数量
        { wch: 12 }, // 是否完成补货
        { wch: 20 }, // 更新时间
      ];
      ws["!cols"] = colWidths;

      // 导出文件
      const fileName = `${t("taskManagement.list.productListModal.title")}_${
        task?.shelf
      }_${new Date().toLocaleDateString("zh-CN").replace(/\//g, "")}.xlsx`;
      XLSX.writeFile(wb, fileName);

      message.success(t("taskManagement.list.productListModal.exportSuccess"));
    } catch (error) {
      console.error("导出失败:", error);
      message.error(t("taskManagement.list.productListModal.exportFailed"));
    }
  };

  // 表格列定义
  const columns: ColumnsType<ProductListItem> = [
    {
      title: t("taskManagement.list.productListModal.columns.itemCode"),
      dataIndex: "productCode",
      key: "productCode",
      width: 120,
    },
    {
      title: t("taskManagement.list.productListModal.columns.itemName"),
      dataIndex: "productName",
      key: "productName",
      width: 200,
    },
    {
      title: t("taskManagement.list.productListModal.columns.operationType"),
      dataIndex: "operationType",
      key: "operationType",
      width: 120,
      render: (type: string) => {
        const colorMap = {
          MoveOut: "red",
          MoveIn: "blue",
          Rearrange: "orange",
          "New Items": "green",
        };
        return (
          <Tag color={colorMap[type as keyof typeof colorMap]}>{type}</Tag>
        );
      },
    },
    {
      title: "Level Code",
      dataIndex: "levelCode",
      key: "levelCode",
      width: 100,
      align: "center",
      render: (text) => text || "-",
    },
    {
      title: "Position",
      dataIndex: "targetLayerSeq",
      key: "targetLayerSeq",
      width: 100,
      align: "center",
    },
    {
      title: t("taskManagement.list.productListModal.columns.plannedQuantity"),
      dataIndex: "plannedQuantity",
      key: "plannedQuantity",
      width: 120,
      align: "center",
    },
    {
      title: t("taskManagement.list.productListModal.columns.plannedCount"),
      dataIndex: "plannedCount",
      key: "plannedCount",
      width: 120,
      align: "center",
    },
    {
      title: t("taskManagement.list.productListModal.columns.xyzQuantity"),
      dataIndex: "xyzQuantity",
      key: "xyzQuantity",
      width: 100,
      align: "center",
    },
    {
      title: t("taskManagement.list.productListModal.columns.actualQuantity"),
      dataIndex: "actualQuantity",
      key: "actualQuantity",
      width: 100,
      align: "center",
    },
    {
      title: "Has Diff",
      dataIndex: "hasDiff",
      key: "hasDiff",
      width: 100,
      align: "center",
      render: (hasDiff: boolean) => (
        <Tag color={!hasDiff ? "green" : "red"}>
          {hasDiff
            ? t("taskManagement.list.productListModal.restockStatus.yes")
            : t("taskManagement.list.productListModal.restockStatus.no")}
        </Tag>
      ),
    },
    {
      title: t(
        "taskManagement.list.productListModal.columns.isRestockCompleted"
      ),
      dataIndex: "isRestocked",
      key: "isRestocked",
      width: 120,
      align: "center",
      render: (isRestocked: boolean) => (
        <Tag color={isRestocked ? "green" : "red"}>
          {isRestocked
            ? t("taskManagement.list.productListModal.restockStatus.yes")
            : t("taskManagement.list.productListModal.restockStatus.no")}
        </Tag>
      ),
    },
    {
      title: t("taskManagement.list.productListModal.columns.updateTime"),
      dataIndex: "updateTime",
      key: "updateTime",
      width: 160,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: "Worker Name",
      dataIndex: "workerName",
      key: "workerName",
      width: 160,
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            {t("taskManagement.list.productListModal.title")} - {task?.shelf}
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={
        <Space>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExportExcel}
            disabled={productList.length === 0}
          >
            {t("taskManagement.list.productListModal.exportExcel")}
          </Button>
          <Button onClick={onClose}>
            {t("taskManagement.list.productListModal.close")}
          </Button>
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={productList}
        loading={loading}
        pagination={{
          pageSize: 1000,
          showSizeChanger: true,
          showQuickJumper: true,
          // showTotal: (total) => t("common.pagination.total", { total }),
        }}
        scroll={{ x: 1200, y: 500 }}
        size="small"
      />
    </Modal>
  );
};

export default ProductListModal;